import sanitizeHtml from 'sanitize-html';
import { sanitizeEmbedCss } from './article-css';

declare const sanitizedArticleHtmlBrand: unique symbol;

/** HTML that has passed the article embed allowlist at the rendering boundary. */
export type SanitizedArticleHtml = string & {
  readonly [sanitizedArticleHtmlBrand]: true;
};

export const EMPTY_ARTICLE_HTML_ERROR =
  'No supported HTML remains. Add static text, headings, lists, tables or images; scripts, iframes and external stylesheets cannot be embedded.';

/** Matches a whole `<style>` element, including its contents. */
const STYLE_ELEMENT = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/giu;

/**
 * Pull the CSS out before sanitize-html runs.
 *
 * `style` is one of sanitize-html's `nonTextTags`, so it discards the element
 * *and* its contents, which is why presentation CSS used to vanish. Extracting
 * first means the CSS reaches `sanitizeEmbedCss` and the remaining markup still
 * goes through the tag allowlist untouched.
 *
 * Replacing repeatedly rather than once, because one pass can reveal a tag that
 * was not there before it: `<sty<style></style>le>` leaves a `<style>` behind
 * when the inner element is removed. Whatever survives the loop still goes
 * through sanitize-html, which discards style elements, so a leftover cannot
 * reach the output, but it would otherwise skip scoping and be dropped silently.
 */
function extractStyles(source: string): { css: string; html: string } {
  const css: string[] = [];
  let html = source;

  for (let pass = 0; pass < 5; pass += 1) {
    const next = html.replace(STYLE_ELEMENT, (_match, contents: string) => {
      css.push(contents);
      return '';
    });
    if (next === html) break;
    html = next;
  }

  return { css: css.join('\n'), html };
}

/**
 * Scope class for one block's CSS, derived from the CSS itself.
 *
 * Content-addressed rather than random so the same block sanitizes to the same
 * markup every time: the value is compared in tests, stored by Payload's field
 * hook and rendered on the server and the client, none of which can differ.
 * Deriving it per block is what keeps two embeds on one page from styling each
 * other.
 */
function scopeClass(css: string): string {
  return `article-embed-${fnv1a64(css).toString(16).padStart(16, '0').slice(0, 10)}`;
}

/**
 * FNV-1a, 64-bit, standing in for a `node:crypto` digest.
 *
 * This module is reachable from the client bundle -- a route component imports
 * `lib/payload`, which imports this file -- so `node:crypto` here fails Start's
 * import protection at build time, and the build is what deploys. An async Web
 * Crypto digest is not an option either: `scopeClass` runs inside sanitization,
 * on the server and again on the client, and both have to reach the same string
 * synchronously.
 *
 * Non-cryptographic is fine because the class is an identifier, not the trust
 * boundary. Containment is the selector prefixing and declaration filtering in
 * `article-css.ts`; this only has to be stable and collision-free across the few
 * blocks on one page, and an author cannot gain anything by colliding with their
 * own other block.
 */
function fnv1a64(value: string): bigint {
  let hash = 0xcbf29ce484222325n;

  // UTF-8 bytes rather than code units, so the digest does not depend on how the
  // runtime happens to represent the string.
  for (const byte of new TextEncoder().encode(value)) {
    hash = ((hash ^ BigInt(byte)) * 0x100000001b3n) & 0xffffffffffffffffn;
  }

  return hash;
}

/**
 * Author classes are allowed wherever a class is meaningful, because the block's
 * own CSS is scoped to the block, a class name cannot reach the rest of the page,
 * and hooks like `.syp-rate-table` are how an embed styles itself.
 */
const CLASS_ONLY_TAGS = [
  'div',
  'span',
  'p',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'ul',
  'ol',
  'li',
  'h2',
  'h3',
  'h4',
  'figure',
  'figcaption',
  'blockquote',
];

const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  ...Object.fromEntries(CLASS_ONLY_TAGS.map((tag) => [tag, ['class']])),
  a: ['class', 'href', 'rel', 'target', 'title'],
  img: ['alt', 'class', 'height', 'loading', 'src', 'width'],
  td: ['class', 'colspan', 'headers', 'rowspan'],
  th: ['abbr', 'class', 'colspan', 'headers', 'rowspan', 'scope'],
};

/**
 * The sole trust boundary for CMS-authored HTML.
 *
 * Keep this server-only in practice: callers pass the branded result to the
 * article renderer, and no component accepts an untrusted string for
 * `dangerouslySetInnerHTML`.
 */
export function sanitizeArticleHtml(value: unknown): SanitizedArticleHtml {
  const source = typeof value === 'string' ? value : '';
  const { css, html: markup } = extractStyles(source);
  const scope = scopeClass(css);
  const scopedCss = sanitizeEmbedCss(css, scope);

  const sanitized = sanitizeHtml(markup, {
    allowedTags: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'h2',
      'h3',
      'h4',
      'ul',
      'ol',
      'li',
      'blockquote',
      'a',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'figure',
      'figcaption',
      'img',
      'div',
      'span',
    ],
    allowedAttributes: ALLOWED_ATTRIBUTES,
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: {
      a: ['http', 'https', 'mailto', 'tel'],
      img: ['http', 'https'],
    },
    allowProtocolRelative: false,
    disallowedTagsMode: 'discard',
    enforceHtmlBoundary: true,
    transformTags: {
      a: (_tagName, attribs) => ({
        tagName: 'a',
        attribs:
          attribs.target?.toLowerCase() === '_blank'
            ? { ...attribs, rel: 'noopener noreferrer' }
            : attribs,
      }),
      img: (_tagName, attribs) => ({
        tagName: 'img',
        attribs: { ...attribs, loading: attribs.loading === 'eager' ? 'eager' : 'lazy' },
      }),
    },
  });

  // The wrapper is what the CSS is scoped to, so it only goes on when there is
  // CSS to apply, otherwise every embed would gain a pointless div.
  if (!scopedCss) return sanitized as SanitizedArticleHtml;

  return `<div class="${scope}"><style>${scopedCss}</style>${sanitized}</div>` as SanitizedArticleHtml;
}

const IMG_SRC = /<img\b[^>]*\bsrc=(?:"([^"]*)"|'([^']*)')/giu;

/**
 * Characters that occupy no visual space. `String.trim` only removes Unicode
 * whitespace, so entities such as `&#8203;` (zero-width space) survive it and
 * would otherwise read as content.
 */
const INVISIBLE = /[\s\p{Default_Ignorable_Code_Point}]/gu;

/**
 * Avoid turning an unsupported embed into an apparently successful blank block.
 * Payload runs field hooks before field validation, so this normally receives the
 * sanitized value; sanitizing here as well keeps direct callers and future hook
 * ordering changes safe.
 */
export function validateArticleHtml(value: unknown): true | string {
  const html = sanitizeArticleHtml(value).trim();
  const text = sanitizeHtml(html, { allowedTags: [], allowedAttributes: {} }).replace(
    INVISIBLE,
    '',
  );
  // A blank or whitespace-only src renders nothing, so it must not count as content.
  const hasImage = Array.from(html.matchAll(IMG_SRC)).some(
    (match) => (match[1] ?? match[2] ?? '').trim() !== '',
  );
  return text || hasImage ? true : EMPTY_ARTICLE_HTML_ERROR;
}
